// Service Worker Registration with comprehensive error handling and fallbacks

let registrationAttempts = 0
const MAX_REGISTRATION_ATTEMPTS = 3
const REGISTRATION_RETRY_DELAY = 2000 // 2 seconds

// Track registration state
let registrationState = {
  registered: false,
  error: null,
  registration: null,
}

/**
 * Check if service workers are supported
 */
function isServiceWorkerSupported() {
  return (
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    (window.location.protocol === 'https:' ||
      window.location.hostname === 'localhost')
  )
}

/**
 * Unregister any broken or old service workers
 */
async function cleanupOldServiceWorkers() {
  try {
    if (!isServiceWorkerSupported()) return

    const registrations = await navigator.serviceWorker.getRegistrations()

    for (const registration of registrations) {
      // Check if service worker is active and working
      if (registration.active) {
        try {
          // Try to ping the service worker
          const response = await fetch('/sw.js', { method: 'HEAD' })
          if (!response.ok) {
            await registration.unregister()
          }
        } catch (e) {
          // Service worker might be broken, unregister it
          try {
            await registration.unregister()
          } catch (unregisterError) {
            // Silent fail
          }
        }
      }
    }
  } catch (e) {
    // Silent fail - cleanup is best effort
  }
}

/**
 * Register service worker with retry logic
 */
async function registerServiceWorkerInternal() {
  if (!isServiceWorkerSupported()) {
    return { success: false, reason: 'not_supported' }
  }

  try {
    // Clean up old workers first
    await cleanupOldServiceWorkers()

    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    })

    // Wait for service worker to be ready
    await navigator.serviceWorker.ready

    registrationState = {
      registered: true,
      error: null,
      registration,
    }

    // Set up update checking
    setupUpdateChecking(registration)

    return { success: true, registration }
  } catch (error) {
    registrationState = {
      registered: false,
      error: error.message,
      registration: null,
    }

    return { success: false, reason: 'registration_failed', error }
  }
}

/**
 * Register service worker with retry logic
 */
export async function registerServiceWorker() {
  // Don't register if already registered
  if (registrationState.registered) {
    return registrationState
  }

  // Reset attempts if we're starting fresh
  if (registrationAttempts === 0) {
    registrationAttempts = 0
  }

  // Try registration
  let result = await registerServiceWorkerInternal()

  // Retry on failure
  while (!result.success && registrationAttempts < MAX_REGISTRATION_ATTEMPTS) {
    registrationAttempts++

    // Wait before retry
    await new Promise((resolve) =>
      setTimeout(resolve, REGISTRATION_RETRY_DELAY * registrationAttempts)
    )

    result = await registerServiceWorkerInternal()
  }

  if (result.success) {
    registrationAttempts = 0 // Reset on success
  }

  return registrationState
}

/**
 * Set up periodic update checking
 */
function setupUpdateChecking(registration) {
  // Check for updates every hour
  setInterval(() => {
    try {
      registration.update()
    } catch (e) {
      // Silent fail
    }
  }, 60 * 60 * 1000) // 1 hour

  // Also check on page visibility change
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        try {
          registration.update()
        } catch (e) {
          // Silent fail
        }
      }
    })
  }

  // Listen for service worker updates
  registration.addEventListener('updatefound', () => {
    const newWorker = registration.installing
    if (newWorker) {
      newWorker.addEventListener('statechange', () => {
        if (
          newWorker.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          // New service worker available, but don't force update
          // Let it activate naturally
        }
      })
    }
  })
}

/**
 * Get current registration state
 */
export function getRegistrationState() {
  return { ...registrationState }
}

/**
 * Force update check
 */
export async function checkForUpdates() {
  try {
    if (registrationState.registration) {
      await registrationState.registration.update()
      return true
    }
    return false
  } catch (e) {
    return false
  }
}

/**
 * Unregister service worker (for debugging)
 */
export async function unregisterServiceWorker() {
  try {
    if (registrationState.registration) {
      await registrationState.registration.unregister()
      registrationState = {
        registered: false,
        error: null,
        registration: null,
      }
      return true
    }
    return false
  } catch (e) {
    return false
  }
}

// Auto-register on import in production (or with flag)
if (typeof window !== 'undefined') {
  const isProduction = process.env.NODE_ENV === 'production'
  const enableSW = process.env.REACT_APP_ENABLE_SW !== 'false'

  if (isProduction && enableSW) {
    // Register after page load
    if (document.readyState === 'loading') {
      window.addEventListener('load', () => {
        registerServiceWorker().catch(() => {
          // Silent fail - app continues normally
        })
      })
    } else {
      registerServiceWorker().catch(() => {
        // Silent fail - app continues normally
      })
    }
  }
}
